import { z } from "zod";

import { toDate } from "../event-experience/agenda-schedule";

/**
 * The fields DELETE_EVENT_ANNOUNCEMENT stamps on a sent announcement's
 * command and on the booking it came from, and what they add up to
 * (functions/python/app/application/service/event_announcement_deletion.py).
 */

/** What "Delete for everyone" has done to one announcement. */
export type AnnouncementDeletion =
  | { state: "none" }
  | { state: "deleting" }
  // The run gave up after its fence (the re-drive's attempt cap). Some
  // copies may be left; a new delete command takes over.
  | { state: "stopped"; reason: string }
  | {
      state: "deleted";
      at: Date | null;
      byName: string | null;
      count: number | null;
    };

export const deletionFieldsSchema = z.object({
  deletionRequestedAt: z.any().optional(),
  deletionStoppedAt: z.any().optional(),
  deletionError: z.string().optional(),
  deletedAt: z.any().optional(),
  deletedByName: z.string().nullable().optional(),
  deletedCount: z.number().optional(),
});

export function deletionOf(
  fields: z.infer<typeof deletionFieldsSchema>,
): AnnouncementDeletion {
  if (fields.deletedAt != null)
    return {
      state: "deleted",
      at: toDate(fields.deletedAt),
      byName: fields.deletedByName ?? null,
      count: fields.deletedCount ?? null,
    };
  if (fields.deletionStoppedAt != null)
    return {
      state: "stopped",
      reason: fields.deletionError ?? "The deletion did not finish.",
    };
  if (fields.deletionRequestedAt != null) return { state: "deleting" };
  return { state: "none" };
}

/** Whether a row may offer "Delete for everyone" (or "Try again"). */
export function mayDelete(deletion: AnnouncementDeletion | undefined): boolean {
  return !deletion || deletion.state === "none" || deletion.state === "stopped";
}
